import React, { useEffect, useState } from "react";
import {
    Alert, Avatar, Box, Button, Chip, CircularProgress, Divider, IconButton, Stack, Tooltip, Typography,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import axios from "axios";
import Api from "../../customlib/Api";
import Global from "../../customlib/Global";
import livechatApi from "./livechatApi";
import { LIVECHAT_PARTNER_BRAND, CORPORATE_BRAND } from "./livechatConstants";
import { getAvatarColor, getInitials } from "./livechatDisplayUtils";
import SubOrderChip from "../CustomerProfile/SubOrderChip";
import { PASTEL } from "./livechatTheme";
import { CustomerFlagInsightSection, Row, SelfOrderFlagBadge, useCustomerFlagInsight } from "./CustomerFlagSection";
import PoinCheckoutLinkSection from "../CustomerProfile/PoinCheckoutLinkSection";

// Riwayat order (FR-A-07/08) — reuse endpoint Lavender "profile-customer" yang sama dengan
// yang dipakai src/components/CustomerProfile/index.jsx#loadCustomerQna (data.data.lastOrder).
// Bukan endpoint POS terpisah — cuma order terakhir, bukan daftar riwayat lengkap.
//
// DEV-1198 — channel mitra-purchasing TIDAK pakai lookup ini (nomor HP mitra bukan nomor
// pelanggan di sistem order, hasilnya kalau ada cuma kebetulan cocok/salah data). Untuk channel
// itu, panel order dibaca dari `session.linkedOrderNumber` via endpoint order-info khusus
// (lihat OrderInfoSection di bawah).
// DEV-1234 — Level Akses: canViewOrderInfo (session.orderInfo) & canGenerateLink (session.generateLink),
// dihitung index.jsx per sesi (corporate & mitra selalu true).
export default function CustomerContextPanel({ session, onGenerateLink, onClose, canViewOrderInfo = true, canGenerateLink = true }) {
    const isMitraChannel = session?.brand === LIVECHAT_PARTNER_BRAND;
    // DEV-1227 (AC DEV-1222) — tanda & info customer tampil "dari layanan manapun SELAIN
    // Corporate"; mitra-purchasing juga bukan konteks customer Sales, jadi ikut dilewati.
    const isCorporateChannel = session?.brand === CORPORATE_BRAND;
    const [lastOrder, setLastOrder] = useState(null);
    const [isLoadingOrder, setIsLoadingOrder] = useState(false);

    useEffect(() => {
        if (isMitraChannel || !canViewOrderInfo || !session?.customerPhone) {
            setLastOrder(null);
            return;
        }
        // Guard respons basi: agent pindah sesi sebelum request selesai -> respons nomor lama
        // diabaikan, tidak menimpa data pelanggan yang sedang dibuka.
        let cancelled = false;
        setIsLoadingOrder(true);
        setLastOrder(null);
        axios
            .get(Api.lavenderGetCustomerQna(session.customerPhone), Api.getRequestHeaderNoAuth())
            .then(({ data }) => { if (!cancelled) setLastOrder(data?.data?.lastOrder || null); })
            .catch(() => { if (!cancelled) setLastOrder(null); })
            .finally(() => { if (!cancelled) setIsLoadingOrder(false); });
        return () => { cancelled = true; };
    }, [isMitraChannel, canViewOrderInfo, session?.customerPhone]);

    // DEV-1227 - flag/insight di-fetch di sini (bukan di dalam CustomerFlagInsightSection) supaya
    // badge "Tidak Bersedia Self Order" di header (sesuai mockup BSR-126 AC-3) bisa dipasang tanpa
    // fetch dobel.
    const showFlagInsight = !isMitraChannel && !isCorporateChannel;
    const { flag, insight, isLoading: isLoadingFlag, refetchFlag } = useCustomerFlagInsight(
        session?.customerPhone,
        showFlagInsight
    );

    if (!session) return null;

    return (
        <Box sx={{
            width: 280, flexShrink: 0, borderLeft: `1px solid ${PASTEL.border}`, backgroundColor: PASTEL.surface,
            display: "flex", flexDirection: "column",
        }}>
            <Box sx={{ p: 2.5, pt: 1.5, textAlign: "center", borderBottom: `1px solid ${PASTEL.border}`, position: "relative" }}>
                {onClose && (
                    <Tooltip title="Sembunyikan panel">
                        <IconButton
                            size="small" onClick={onClose}
                            sx={{ position: "absolute", top: 4, right: 4, color: "text.secondary" }}
                        >
                            <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                <Avatar sx={{ bgcolor: getAvatarColor(session.customerName), width: 56, height: 56, fontSize: 20, mx: "auto", mb: 1 }}>
                    {getInitials(session.customerName)}
                </Avatar>
                <Typography variant="subtitle1" fontWeight={700}>{session.customerName}</Typography>
                <Typography variant="body2" color="text.secondary">{session.customerPhone}</Typography>
                {/* Mockup BSR-126 AC-3 - customer_id sudah dikembalikan getFlag() (backend resolve
                    dari phone), cuma belum ditampilkan. Pakai id asli sistem (integer polos),
                    BUKAN format "CUST-XXXXXX" di mockup - itu placeholder desain, bukan format nyata. */}
                {showFlagInsight && flag?.customer_id && (
                    <Typography variant="caption" sx={{ color: PASTEL.primary, fontWeight: 600, display: "block" }}>
                        Customer ID: #{flag.customer_id}
                    </Typography>
                )}
                {/* Mockup BSR-126 AC-3 - badge menonjol saat customer ditandai "tidak bersedia pesan
                    sendiri", supaya Sales langsung sadar tanpa perlu scroll ke Tanda Customer. */}
                {showFlagInsight && <SelfOrderFlagBadge flag={flag} sx={{ mt: 1 }} />}
            </Box>

            <Box sx={{ p: 2, flex: 1, overflowY: "auto" }}>
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Informasi Kontak
                </Typography>
                <Stack spacing={1.5} sx={{ mt: 1, mb: 2 }}>
                    <Row label="Kanal" value={session.brand} />
                    {session.issueCategory && <Row label="Kategori Kendala" value={session.issueCategory} />}
                    <Row label="Chat pertama" value={Global.formatDateTimeToStrDateTime24(session.created_at)} />
                    {session.originUrl && <Row label="Halaman asal" value={session.originUrl} isLink />}
                    <Row
                        label="Status"
                        value={
                            <Chip
                                size="small"
                                label={session.status === "active" ? "Aktif" : "Selesai"}
                                sx={session.status === "active"
                                    ? { fontWeight: 600, mt: 0.25, backgroundColor: PASTEL.primary, color: "#fff" }
                                    : { fontWeight: 600, mt: 0.25 }}
                            />
                        }
                    />
                    {/* MTN-956 — penerima lead pertama (permanen) & pengalihan terakhir oleh Tim CS. */}
                    {session.firstAssignedAgentName && (
                        <Row
                            label="Penerima lead pertama"
                            value={`${session.firstAssignedAgentName}${session.firstAssignedAt ? ` · ${Global.formatDateTimeToStrDateTime24(session.firstAssignedAt)}` : ""}`}
                        />
                    )}
                    {session.lastTransfer && (
                        <Row
                            label="Dialihkan ke"
                            value={`${session.lastTransfer.toAgentName} oleh ${session.lastTransfer.byAgentName || "Tim CS"}`}
                        />
                    )}
                </Stack>

                {showFlagInsight && (
                    <>
                        <Divider sx={{ mb: 2 }} />
                        <CustomerFlagInsightSection
                            flag={flag} insight={insight} isLoading={isLoadingFlag}
                            phone={session.customerPhone} onFlagChanged={refetchFlag}
                        />
                    </>
                )}

                {/* Mockup BSR-126 AC-3 - "Riwayat belanja": nilai + jumlah order yang SUDAH DIBAYAR,
                    beda dari order_history.total_orders (semua order apapun statusnya). */}
                {showFlagInsight && canViewOrderInfo && insight?.order_history && (
                    <>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                            Riwayat Belanja
                        </Typography>
                        <Stack direction="row" spacing={2} sx={{ mt: 1, mb: 2 }}>
                            <Row
                                label="Nilai Belanja"
                                value={`Rp ${new Intl.NumberFormat("id-ID").format(insight.order_history.total_spent ?? 0)}`}
                            />
                            <Row label="Order Dibayar" value={insight.order_history.paid_orders ?? 0} />
                        </Stack>
                    </>
                )}

                {canViewOrderInfo && <Divider sx={{ mb: 2 }} />}

                {!canViewOrderInfo ? null : isMitraChannel ? (
                    <OrderInfoSection session={session} />
                ) : (
                    <>
                        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                            Order Terakhir
                        </Typography>

                        {isLoadingOrder && (
                            <Box textAlign="center" sx={{ py: 2 }}>
                                <CircularProgress size={20} />
                            </Box>
                        )}

                        {!isLoadingOrder && !lastOrder && (
                            <Alert
                                severity="info" variant="outlined" icon={<InfoOutlinedIcon fontSize="small" />}
                                sx={{ borderRadius: 2, fontSize: 12, mt: 1, mb: 2 }}
                            >
                                Belum ada riwayat order untuk nomor ini.
                            </Alert>
                        )}

                        {!isLoadingOrder && lastOrder && (
                            <Box sx={{ mt: 1, mb: 2 }}>
                                <Box textAlign="center" sx={{ mb: 1.5 }}>
                                    <Avatar
                                        variant="rounded"
                                        src={`${Api.getLavenderUrl()}/${lastOrder?.cartLast?.image}?w=200&fit=crop&auto=format`}
                                        sx={{ width: 72, height: 72, mx: "auto", mb: 1 }}
                                    />
                                    {lastOrder?.cartLast?.product_code && (
                                        <Chip size="small" label={lastOrder.cartLast.product_code} variant="outlined" />
                                    )}
                                </Box>
                                <Stack spacing={1.25}>
                                    <Row
                                        label="Nomor CO"
                                        value={
                                            <Button
                                                size="small" variant="text" target="_blank" rel="noreferrer"
                                                href={`https://sakura.prestisa.id/orders/view/${lastOrder?.order_number}`}
                                                endIcon={<OpenInNewIcon sx={{ width: 12, height: 12 }} />}
                                                sx={{ fontSize: 12, textTransform: "none", p: 0, minWidth: 0, color: PASTEL.primary }}
                                            >
                                                {lastOrder?.order_number}
                                            </Button>
                                        }
                                    />
                                    {/* DEV-1426 */}
                                    <Row label="Sub Order" value={<SubOrderChip subOrder={lastOrder?.sub_order} />} />
                                    <Row label="Nama Pengirim" value={lastOrder?.cartLast?.sender_name || "-"} />
                                    <Row label="Pembayaran" value={lastOrder?.payment_type || "-"} />
                                    <Row label="Total" value={`Rp ${new Intl.NumberFormat("id-ID").format(lastOrder?.total || 0)}`} />
                                    <Row label="Qty" value={lastOrder?.cartLast?.qty ?? "-"} />
                                </Stack>
                            </Box>
                        )}

                        {/* DEV-981 — Link Tukar Diskon, sama dengan blok di CustomerProfile (chat WA).
                            customer_id diambil dari lastOrder (Lavender profile-customer). */}
                        {!isLoadingOrder && lastOrder?.customer_id > 0 && (
                            <PoinCheckoutLinkSection customerId={Number(lastOrder.customer_id)} />
                        )}
                    </>
                )}

                {canGenerateLink && <Divider sx={{ mb: 2 }} />}

                {canGenerateLink && <Button
                    size="small" variant="outlined" fullWidth onClick={onGenerateLink}
                    startIcon={<LinkIcon fontSize="small" />}
                    sx={{
                        textTransform: "none",
                        color: PASTEL.primary,
                        borderColor: PASTEL.primary,
                        "&:hover": { borderColor: PASTEL.primaryDark, backgroundColor: PASTEL.primarySoft },
                    }}
                >
                    Generate Link Lanjut Chat
                </Button>}
            </Box>
        </Box>
    );
}

// DEV-1198 (AC-5/AC-5b) — panel order/status pembayaran/ringkasan revisi untuk channel
// mitra-purchasing, dibaca dari `session.linkedOrderNumber` (bukan customerPhone, lihat komentar
// di atas komponen utama). `revisionNote` bisa null walau statusnya "on revision" — banyak record
// lama tidak punya bentuk log yang cocok utk diekstrak (lihat livechat.order-info.service.ts).
function OrderInfoSection({ session }) {
    const [orderInfo, setOrderInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!session?._id) {
            setOrderInfo(null);
            return;
        }
        let cancelled = false; // guard respons basi saat pindah sesi, lihat efek lastOrder di atas
        setIsLoading(true);
        setOrderInfo(null);
        livechatApi
            .getOrderInfo(session._id)
            .then(({ data }) => { if (!cancelled) setOrderInfo(data?.data?.orderInfo || null); })
            .catch(() => { if (!cancelled) setOrderInfo(null); })
            .finally(() => { if (!cancelled) setIsLoading(false); });
        return () => { cancelled = true; };
    }, [session?._id]);

    return (
        <>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                Info Order Terkait
            </Typography>

            {isLoading && (
                <Box textAlign="center" sx={{ py: 2 }}>
                    <CircularProgress size={20} />
                </Box>
            )}

            {!isLoading && !orderInfo && (
                <Alert
                    severity="info" variant="outlined" icon={<InfoOutlinedIcon fontSize="small" />}
                    sx={{ borderRadius: 2, fontSize: 12, mt: 1, mb: 2 }}
                >
                    {session.linkedOrderNumber
                        ? "Order yang dikaitkan tidak ditemukan."
                        : "Mitra tidak mengaitkan order ke percakapan ini."}
                </Alert>
            )}

            {!isLoading && orderInfo && (
                <Stack spacing={1.25} sx={{ mt: 1, mb: 2 }}>
                    <Row label="Nomor PO" value={orderInfo.poId ? `PO-${orderInfo.poId}` : "-"} />
                    <Row label="Status PO" value={<Chip size="small" label={orderInfo.status} variant="outlined" />} />
                    <Row label="Pembayaran" value={orderInfo.paymentType || "-"} />
                    <Row label="Total" value={`Rp ${new Intl.NumberFormat("id-ID").format(orderInfo.total || 0)}`} />
                    <Row label="Jadwal Kirim" value={orderInfo.deliveryAt ? Global.formatDateTimeToStrDateTime24(orderInfo.deliveryAt) : "-"} />
                    {orderInfo.revisionNote && (
                        <Row label="Catatan Revisi" value={orderInfo.revisionNote} />
                    )}
                </Stack>
            )}
        </>
    );
}
